import { UrlUtils } from "@stellar/utils";
import { UlmClientType } from "@custom-types/ulm-types";
import { CLIENT_CONFIGURATION, ULM_CLIENTS } from "@constants/ulm-constants";
import { UlmConfigState } from "@store/ulm-config/ulm-config-slice";
import { QueryParams } from "@router/route-params";
import { runtimeConfig } from "@src/runtime-config";
import { redirectClient } from "@utils/client-utils";
import { isEmbedded, requestParentToRedirect } from "@utils/browser-utils";
import { getFaroCoreApiClient } from "@api/core-api/core-api-utils";
import { codeToLanguageCodes, LanguageCodes } from "@flat-ui";
import {
  CoreApiClient,
  FaroCoreApiConst,
  FaroCoreApiTypes,
} from "@stellar/core-api-client";
import { getUrlBasedOnServer } from "@utils/servers-utils";
import { ActiveServer } from "@custom-types/server-types";

const LOCALIZE_LANGUAGE_COOKIE_NAME = "localize-language";

function getSharedLanguageCookie(): string | null {
  const cookiePrefix = `${LOCALIZE_LANGUAGE_COOKIE_NAME}=`;
  const cookies = document.cookie.split(";");

  for (const cookie of cookies) {
    const trimmedCookie = cookie.trim();
    if (trimmedCookie.startsWith(cookiePrefix)) {
      const value = trimmedCookie.slice(cookiePrefix.length);
      try {
        return decodeURIComponent(value);
      } catch {
        return value;
      }
    }
  }

  return null;
}

/**
 * @returns True if the ULM client is a string and is included in the ULM clients list
 */
export function isValidUlmClient(
  client: string | null
): client is UlmClientType {
  if (!client) {
    return false;
  }

  return Object.values<string>(ULM_CLIENTS).includes(client);
}

/**
 * @returns True if the server identifier string is of type EServerIdentifier
 */
export function isValidServerIdentifier(
  identifier: string
): identifier is FaroCoreApiTypes.ServerIdentifier {
  const allServerIdentifiers: string[] = FaroCoreApiConst.allServerIdentifiers;
  return allServerIdentifiers.includes(identifier);
}

/**
 * Returns the ULM configuration based on the "client" and "lang" query parameters.
 */
export function getUlmConfig(): UlmConfigState {
  const searchParams = new URLSearchParams(window.location.search);
  const clientParam = searchParams.get(QueryParams.client);
  const languageParam = searchParams.get(QueryParams.lang);
  const sharedLanguageCookie = getSharedLanguageCookie();
  const localizeLanguage = window.localStorage.getItem("ljs-lang");

  return {
    client: isValidUlmClient(clientParam) ? clientParam : "default",
    language: codeToLanguageCodes(
      languageParam ??
        sharedLanguageCookie ??
        localizeLanguage ??
        LanguageCodes.English
    ),
  };
}

interface FilterActiveServersProps {
  /** Servers where user has an active account */
  activeServers: ActiveServer[];

  /** Server Option. If passed it will be the only available server option */
  serverOption?: FaroCoreApiTypes.ServerIdentifier;
}

/**
 * @returns the active servers filtered by the available login options
 *
 * @param activeServers servers with active status for the user email
 * @param configFlags Config flags from the client options
 * @param serverOption Server Option that if defined it will be the only available server option
 */
export function filterActiveServers({
  activeServers,
  serverOption,
}: FilterActiveServersProps): ActiveServer[] {
  const availableServers: FaroCoreApiTypes.ServerIdentifier[] = [];

  // If the serverOption is defined set that option only
  if (serverOption) {
    availableServers.push(serverOption);
  } else {
    availableServers.push("sphere_2_com");
    availableServers.push("sphere_2_eu");
  }

  // Filter out active servers not available due to the ULM client config options
  return activeServers.filter((server) =>
    availableServers.includes(server.identifier)
  );
}

/** Returns FaroCoreApiClient based on the selected server */
export function getFaroCoreApiClientBasedOnServer(
  selectedServer: FaroCoreApiTypes.ServerIdentifier
): CoreApiClient {
  const { apiBaseUrl } = getUrlBasedOnServer(selectedServer);

  return getFaroCoreApiClient(apiBaseUrl);
}

/** Returns true if the passed server uses third party login provider */
export function isSelectedServerUseThirdPartyLogin(
  selectedServer: ActiveServer
): boolean {
  return selectedServer.loginProvider.identifier !== "email";
}

/**
 * Handles the redirection after a successful login
 * @param url redirect URL
 * @param client ULM client
 * @param selectedServer Selected server
 * @throws {Error} if the passed url is invalid
 */
export function handleRedirectAfterLogin(
  url: string,
  client: UlmClientType,
  selectedServer: FaroCoreApiTypes.ServerIdentifier
): void {
  const urlObject = UrlUtils.constructUrl({
    url,
  });

  // Throw error if there is no valid URL
  if (!urlObject) {
    throw new Error("Invalid Sphere 2.0 login redirect URL");
  }

  // For clients that embed the ULM in an iFrame we send a window message to the
  // parent window so the parent window handles the redirect themselves, avoiding
  // potential redirect security issues in browsers.
  // For any other client we simply redirect from the ULM.
  if (isEmbedded(client)) {
    requestParentToRedirect(urlObject);
  } else {
    // Redirect to the url with core api
    const { apiBaseUrl } = getUrlBasedOnServer(selectedServer);

    const redirectUrl = `${apiBaseUrl}/redirectTo?redirectUrl=${encodeURIComponent(
      urlObject.href
    )}`;

    UrlUtils.redirectToUrl(redirectUrl);
  }
}

interface RedirectToAnotherServerLoginPage {
  /** Server that user already selected */
  selectedServer: FaroCoreApiTypes.ServerIdentifier;

  /** Sanitized email address */
  email: string;

  /** Client which is using the ULM */
  client: UlmClientType;
}

/** Redirect users to another SXG server with login info in query params */
export function redirectToAnotherServerLoginPage({
  selectedServer,
  email,
  client,
}: RedirectToAnotherServerLoginPage): void {
  // Use "set" to overwrite email and selectedServer query params
  // to avoid duplicate query params with same key
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.set(QueryParams.email, email);
  searchParams.set(QueryParams.preselectedServer, selectedServer);

  // Remove "parent_url" query param since there won't be a parent window anymore
  searchParams.delete(QueryParams.parentUrl);

  const { sphereEntryPageUrl } = getUrlBasedOnServer(selectedServer);

  redirectClient({
    client,
    redirectUrl: `${sphereEntryPageUrl}?${searchParams.toString()}`,
  });
}

/**
 * Returns the visited server identifier based on the current ULM instance
 */
export function getVisitedServerIdentifier(): FaroCoreApiTypes.ServerIdentifier | null {
  const appEnv = runtimeConfig.appEnv;
  const domainIdentifier = appEnv.split("-").shift();

  switch (domainIdentifier) {
    case "com":
      return "sphere_2_com";
    case "eu":
      return "sphere_2_eu";
    default:
      return null;
  }
}

/**
 * @returns a redirect URL taken from the client configuration or the URL search params.
 * The order of priority to read the redirect URL should be the following
 * - The client configuration redirect URL
 * - The server specific redirect URL query parameter
 * - The general redirect URL query parameter
 * @param searchParams Current URL search params
 * @param server Current server identifier
 * @param client Current ULM client
 */
export function getRedirectUrlParam(
  searchParams: URLSearchParams,
  server: FaroCoreApiTypes.ServerIdentifier,
  client: UlmClientType
): string | null {
  const clientRedirectUrl =
    server === "sphere_2_com"
      ? CLIENT_CONFIGURATION[client].redirectUrlCom
      : CLIENT_CONFIGURATION[client].redirectUrlEu;

  if (clientRedirectUrl) {
    return clientRedirectUrl;
  }

  const serverRedirectUrlParam =
    server === "sphere_2_com"
      ? searchParams.get(QueryParams.redirectUrlS2Com)
      : searchParams.get(QueryParams.redirectUrlS2Eu);

  const generalRedirectUrlParam = searchParams.get(QueryParams.redirectUrl);

  return serverRedirectUrlParam || generalRedirectUrlParam;
}
